<script setup lang="ts">
import LoadingModal from "@/components/LoadingModal.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledTable from "@/components/StyledTable.vue";
import StyledTextarea from "@/components/StyledTextarea.vue";
import useAuthUser from "@/composables/useAuthUser";
import useCompanyComment from "@/modules/companies/composables/useCompanyComment";
import { CompanyEditTab } from "@/modules/companies/interfaces/companyEditTab";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, maxLength, minLength, required } from "@vuelidate/validators";
import moment from "moment";
import { computed, inject, reactive } from "vue";
import { useToast } from "vue-toastification";

interface Props extends CompanyEditTab {}
const props = defineProps<Props>();

const { data, isLoading, createCompanyCommentAsync, refetch } =
  useCompanyComment({
    params: {
      companyId: props.company.id,
    },
    immediate: true,
  });

const invalidateQueryData = async () => {
  refetch();
};

const headers = {
  id: "Id",
  createdAt: "Fecha",
  creator: "Usuario",
  commentary: "Comentario",
};

const comment = reactive({
  commentary: "",
});

const rules = {
  commentary: {
    maxLengthValue: helpers.withMessage(
      "El comentario debe ser de 10 caracteres",
      minLength(10),
    ),
    required: helpers.withMessage("El comentario es requerido", required),
  },
};

const v$ = useVuelidate(rules, comment);

const toast = useToast();
const { user } = useAuthUser();

const comments = computed(() => {
  return [...data.value]
    ?.sort((a, b) => {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    })
    .map((comment) => ({
      ...comment,
      createdAt: moment(comment.createdAt).format("LLL"),
      creator: `${comment.creatorName}`,
    }));
});

const handleCreateComment = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message, {
        timeout: 2500,
      });
      return;
    }
  }

  try {
    await createCompanyCommentAsync({
      params: {
        creatorId: user.value?.id!,
        companyId: props.company.id,
        ...comment,
      },
    });

    toast.success("Comentario agregado correctamente");
    comment.commentary = "";
  } catch (err: any) {
    toast.error(err.response.data.message, {
      timeout: 2500,
    });
  } finally {
    invalidateQueryData();
  }
};
</script>

<template>
  <template v-if="!isLoading">
    <StyledTable
      title="Comentarios generales"
      create-toggle-title="Agregar comentario"
      on-delete-title="¿Está seguro de eliminar este comentario?"
      custom-grid-class="grid gap-x-2 grid-cols-[repeat(6,_minmax(144px,_1fr))]"
      :headers="headers"
      :entries="comments"
      :on-create="handleCreateComment"
      :show-save-button="false"
    >
      <template #form>
        <StyledTextarea
          class="!w-full"
          v-model="comment.commentary"
          label="Comentario"
          placeholder="¿Qué ha sucedido?"
        />

        <StyledButton
          class="bg-[#253C70] text-white h-fit ml-auto !p-[0.65rem] !py-[0.25rem]"
        >
          <span class="material-symbols-outlined text-2xl"> save </span>
          <p>Guardar</p>
        </StyledButton>
      </template>

      <template #entry="{ key, entryField }">
        <span :class="[key == 'commentary' && 'col-span-3']">{{
          entryField
        }}</span>
      </template>
    </StyledTable>
  </template>
  <LoadingModal :when="isLoading" />
</template>

<style></style>
